"use client";

import { Core } from "@faire/design-tokens";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { ColorOrKey } from "@faire/web--source/slate/Color";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { LinkStyles } from "@faire/web--source/ui/Link";
import throttle from "lodash/throttle";
import React, { useEffect, useRef, useMemo, useState } from "react";
import styled, { css } from "styled-components";

export interface ExpandableTextProps {
  content: string;
  maxLines: number;
  color?: ColorOrKey;
  ctaColor?: ColorOrKey;
  readMoreBgColor?: ColorOrKey;
  /** Whether this text is able to be collapsed again once expanded. */
  collapsible?: boolean;
  /** Custom label to display for button that expands text. */
  expandButtonLabel?: string;
  /** Custom label to display for button that collapses text. */
  collapseButtonLabel?: string;
  /** Callback that gets called whenever the user expands the text. */
  expandCallback?: () => void;
  /** Callback that gets called whenever the user collapses the text. */
  collapseCallback?: () => void;
}

const RESIZE_THROTTLE_MS = 200;
const LINE_HEIGHT = 20;

export const ExpandableText: React.FC<ExpandableTextProps> = (props) => {
  const [textHeight, setTextHeight] = useState<number>(0);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const {
    content,
    maxLines,
    ctaColor,
    readMoreBgColor,
    collapsible,
    expandButtonLabel,
    collapseButtonLabel,
    color,
  } = props;

  const updateTextHeight = () => {
    setTextHeight(measureRef.current?.clientHeight ?? 0);
  };

  const throttledUpdateTextHeight = throttle(
    updateTextHeight,
    RESIZE_THROTTLE_MS
  );

  useEffect(() => {
    getWindow()?.addEventListener("resize", throttledUpdateTextHeight);
    updateTextHeight();
    return () => {
      getWindow()?.removeEventListener("resize", throttledUpdateTextHeight);
    };
  });

  const onExpand = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded(true);
    props.expandCallback?.();
  };

  const onCollapse = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded(false);
    props.collapseCallback?.();
  };

  const canExpand = useMemo(
    () => !isExpanded && textHeight > LINE_HEIGHT * props.maxLines,
    [textHeight, isExpanded, props.maxLines]
  );
  const measureRef = useRef<HTMLParagraphElement>(null);
  return (
    <div style={{ position: "relative" }}>
      <InvisibleText
        ref={measureRef}
        $maxLines={maxLines}
        $collapsible={collapsible}
        $expanded
        color={color}
      >
        {content}
      </InvisibleText>
      <Text
        $expanded={isExpanded}
        $maxLines={maxLines}
        $collapsible={collapsible}
        color={color}
      >
        {content}
      </Text>
      {isExpanded && collapsible ? (
        <LessButton onClick={onCollapse} color={ctaColor}>
          {collapseButtonLabel ? (
            collapseButtonLabel
          ) : (
            <StrictLocalMsg
              defaultMessage="Close"
              description={{ text: "Close a user interface component." }}
            />
          )}
        </LessButton>
      ) : null}
      {canExpand ? (
        <MoreButton
          $topPosition={(maxLines - 1) * LINE_HEIGHT}
          onClick={onExpand}
          color={ctaColor}
          $readMoreBgColor={readMoreBgColor}
        >
          {expandButtonLabel ? (
            expandButtonLabel
          ) : (
            <StrictLocalMsg
              defaultMessage="Read More"
              description={{ text: "Button to expand a clamped text" }}
            />
          )}
        </MoreButton>
      ) : null}
    </div>
  );
};

const Text = styled.p<{
  $expanded: boolean;
  $maxLines: number;
  $collapsible?: boolean;
}>`
  ${(props) => TypographyStyles({ color: props.color, theme: props.theme })};
  display: -webkit-box;
  -webkit-line-clamp: ${({ $maxLines }) => $maxLines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  ${({ $expanded }) =>
    $expanded
      ? css`
          -webkit-line-clamp: unset;
          -webkit-box-orient: unset;
        `
      : ""};

  ${({ $expanded, $collapsible }) =>
    $expanded && $collapsible
      ? css`
          display: inline;
        `
      : ""};
`;

const InvisibleText = styled(Text)`
  position: absolute;
  bottom: 0;
  opacity: 0;
  z-index: -1;
`;

const MoreButton = styled.div<{
  $topPosition: number;
  $readMoreBgColor?: ColorOrKey;
}>`
  ${(props) => TypographyStyles({ theme: props.theme })}
  ${(props) => LinkStyles({ color: props.color })}
  ${LinkStyles}
  position: absolute;
  top: ${(props) => props.$topPosition}px;
  right: 0;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -40px;
    right: 0;
    bottom: 0;
    z-index: -1;
    cursor: unset;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0),
      ${(props) => props.$readMoreBgColor ?? Core.surface.inverse} 30%
    );
  }
`;

const LessButton = styled.div`
  ${(props) => TypographyStyles({ theme: props.theme })}
  ${(props) => LinkStyles({ color: props.color })}
  display: inline;
  padding-left: ${getSpacing("1x")};
`;
